import {
  useTimeAxisContext,
  useShifts,
  useFormatZoned,
} from "utils/customHooks/genericHooks";
import { useSelector } from "store/useSelector";
import * as d3 from "d3";
import styled, { css } from "styled-components/macro";
import { useIsDarkMode } from "store/atoms";
import { Chip } from "@mui/material";
import {
  darkModeCardBackgroundColor,
  darkModeCardBorderColor,
  darkModeElementBorderColor,
  darkModeModalBackgroundColor,
  darkModeTitleBackgroundColor,
  lightNeutral50,
  lightTextPlaceholder,
  medGrey,
  muiButtonText,
} from "constants/colors";
import { PeopleOutlined } from "@mui/icons-material";
import { useState } from "react";
import { useTimelinesStore } from "./TimelineCardControls/timelinesStore";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";

const MAX_SHIFTS_BEFORE_COLLAPSE_TITLE = 3;
const MAX_SHIFTS_BEFORE_COMPLETELY_COLLAPSE_TITLE = 9;
const MAX_SHIFTS_BEFORE_SUPER_SMALL = 16;

/** vertical line at the shift boundaries + small pill with shift name atop the timeline */
export function ShiftBoundariesIndicators({
  height,
}: {
  height: number;
}): React.JSX.Element | null {
  const visibleRange = useSelector(s => s.legacy.visibleRange);
  const focusRange = useSelector(s => s.legacy.focusRange);
  const shifts = useShifts();

  const { showTimelineShiftIndicators } = useTimelinesStore();
  if (!visibleRange || !focusRange || !showTimelineShiftIndicators) {
    return null;
  }

  const visibleShifts = shifts.filter(
    ({ range }) => visibleRange.ge <= range.ge && range.ge < visibleRange.lt
  );
  // display them in reverse order, so the left ones can overlap the right ones
  const visibleShiftsReversed = visibleShifts.reverse();
  return (
    <g>
      {visibleShiftsReversed.map(({ range: { ge, lt }, shift: { name } }) => (
        <ShiftBoundaryIndicators
          data-testid={`shift-boundaries-indicator-${name}`}
          key={ge}
          shiftName={name}
          numShifts={visibleShifts.length}
          {...{ ge, lt, height }}
        />
      ))}
    </g>
  );
}
const OVERFLOW_BOTTOM_PX = 6;
function ShiftBoundaryIndicators({
  ge,
  lt,
  shiftName,
  height,
  numShifts,
}: {
  ge: number;
  lt: number;
  shiftName?: string;
  height: number;
  numShifts: number;
}) {
  const now = Date.now();
  const isCurrent = ge <= now && now < lt;
  const { scaleTime } = useTimeAxisContext();
  const x0 = scaleTime(ge);

  const collapseTitle = numShifts > MAX_SHIFTS_BEFORE_COLLAPSE_TITLE;
  const tooManyShifts = numShifts > MAX_SHIFTS_BEFORE_COMPLETELY_COLLAPSE_TITLE;
  const supersmall = numShifts > MAX_SHIFTS_BEFORE_SUPER_SMALL;

  const [hover, setHover] = useState(false);
  const hideTitle = !hover && tooManyShifts && !isCurrent;
  const borderTopHeight = hideTitle ? 18 : -27;

  const pathLeft = x0;
  const pathTop = height + OVERFLOW_BOTTOM_PX;

  const lineShiftStart = d3.line()([
    [pathLeft, borderTopHeight],
    [pathLeft, pathTop],
  ]);
  const [isDarkMode] = useIsDarkMode();
  const formatZoned = useFormatZoned();
  const [isTakingSnapshot] = useIsTakingSnapshot();
  return (
    <StyledG
      className="shift-boundaries-indicator"
      data-testid={`shift-boundaries-indicator-${(shiftName ?? "")
        .split(" ")
        .join("_")}`}
      $isDarkMode={isDarkMode}
      {...{
        $isCurrent: isCurrent,
        $collapseTitle: collapseTitle,
        $hideTitle: hideTitle,
        $hover: hover,
        $supersmall: supersmall,
      }}
    >
      <g className="vertical-line">
        <path
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          className="vertical-line-white-padding"
          d={lineShiftStart || ""}
        />
        <path className="shift-vertical-line" d={lineShiftStart || ""} />
      </g>
      {isTakingSnapshot ? null : (
        <StyledForeignObject
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          className="shift-name"
          x={pathLeft}
          y={-20}
          width={1} /* width+height required in Firefox */
          height={1} /* width+height required in Firefox */
          {...{ $collapseTitle: collapseTitle, $isCurrent: isCurrent }}
          $isDarkMode={isDarkMode}
        >
          <Chip
            icon={<PeopleOutlined />}
            label={
              hover
                ? `${shiftName}: ${formatZoned(ge, "EEE HH:mm MMM d")} -
            ${formatZoned(lt, "EEE HH:mm MMM d")}`
                : shiftName
            }
            variant="outlined"
          />
        </StyledForeignObject>
      )}
    </StyledG>
  );
}

const StyledG = styled.g<{
  $isDarkMode: boolean;
  $collapseTitle: boolean;
  $isCurrent: boolean;
  $hideTitle: boolean;
  $hover: boolean;
  $supersmall: boolean;
}>`
  pointer-events: none;
  .vertical-line-white-padding {
    pointer-events: auto;
    cursor: pointer;
  }
  ${p =>
    p.$collapseTitle &&
    !p.$hideTitle &&
    !p.$isCurrent &&
    css`
      .MuiChip-root {
        pointer-events: auto;
        ${p.$hover
          ? css`
              width: fit-content;
              max-width: unset;
              left: 5px;
            `
          : css`
              .MuiChip-label {
                display: none;
              }
              width: 36px;
              .MuiChip-icon {
                margin-left: -12px;
              }
            `}
      }
    `}
  ${p =>
    p.$hideTitle &&
    !p.$hover &&
    css`
      .shift-name {
        display: none;
      }
    `}

  path.shift-vertical-line {
    stroke: ${p => (p.$isDarkMode ? "#b8c9de" : "#102f5b")};
    stroke-width: 2px;
    stroke-linecap: round;
  }
  path.vertical-line-white-padding {
    stroke: ${p => (p.$isDarkMode ? darkModeCardBackgroundColor : "#fff")};
    stroke-width: 6px;
    stroke-linecap: round;
  }
  ${p =>
    p.$supersmall &&
    css`
      path.shift-vertical-line {
        stroke-width: 1px;
        opacity: 0.25;
      }
      path.vertical-line-white-padding {
        stroke-width: 2px;
        opacity: 0.25;
      }
    `}
`;

const StyledForeignObject = styled.foreignObject<{
  $collapseTitle: boolean;
  $isCurrent: boolean;
  $isDarkMode: boolean;
}>`
  overflow: visible;
  .MuiChip-root {
    position: relative !important;
    max-width: unset;
    position: absolute;
    top: -8px;
    left: 5px;
    border-radius: 6px;
    height: fit-content;
    padding: 2px 0 2px 2px;
    color: ${lightTextPlaceholder};
    background: #fff;
    border: 1px solid ${lightNeutral50};
    .MuiSvgIcon-root {
      width: 18px;
      height: 18px;
    }
  }
  ${p =>
    p.$isDarkMode &&
    css`
      color: ${medGrey};
      background: ${darkModeModalBackgroundColor};
      border: 1px solid ${darkModeCardBorderColor};
    `}
  ${p =>
    p.$isCurrent &&
    css`
      .MuiChip-root {
        color: ${muiButtonText};
        background: #fff;
        border: 1px solid ${lightTextPlaceholder};
      }
      ${p.$isDarkMode &&
      css`
        .MuiChip-root {
          color: #fff;
          background: ${darkModeTitleBackgroundColor};
          border: 1px solid ${darkModeElementBorderColor};
        }
        .MuiSvgIcon-root {
          color: ${medGrey};
        }
      `}
    `}
`;
